import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "display",
    "editor",
    "editButton",
    "firstName",
    "lastName",
    "firstNameText",
    "lastNameText",
    "saveButton",
    "status"
  ]

  connect() {
    this.firstNameValue = this.data.get("firstName") || ""
    this.lastNameValue = this.data.get("lastName") || ""
  }

  edit(event) {
    event.preventDefault()
    if (this.busy || this.editing) return

    this.editing = true
    this.firstNameTarget.value = this.firstNameValue
    this.lastNameTarget.value = this.lastNameValue
    this.element.classList.add("is-editing")
    this.displayTarget.classList.add("d-none")
    this.editButtonTarget.classList.add("d-none")
    this.editorTarget.classList.remove("d-none")
    this.clearStatus()

    window.requestAnimationFrame(() => {
      this.firstNameTarget.focus()
      this.firstNameTarget.select()
    })
  }

  cancel(event) {
    if (event) event.preventDefault()
    this.closeEditor()
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeEditor()
      return
    }

    if (event.key !== "Enter") return

    event.preventDefault()
    this.save(event)
  }

  async save(event) {
    if (event) event.preventDefault()
    if (this.busy) return

    const nextFirstName = this.firstNameTarget.value.trim()
    const nextLastName = this.lastNameTarget.value.trim()

    if (nextFirstName === this.firstNameValue && nextLastName === this.lastNameValue) {
      this.closeEditor()
      return
    }

    this.setBusy(true)

    try {
      const response = await fetch(this.data.get("url"), {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          lead: {
            surname: nextFirstName,
            name: nextLastName
          },
          inline_context: "project_table"
        })
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        const errors = payload.errors || ["Speichern fehlgeschlagen."]
        throw new Error(errors.join(" "))
      }

      this.applyValues(
        payload.values && Object.prototype.hasOwnProperty.call(payload.values, "surname") ? payload.values.surname : nextFirstName,
        payload.values && Object.prototype.hasOwnProperty.call(payload.values, "name") ? payload.values.name : nextLastName
      )
      this.closeEditor()
      this.flashSaved()
      this.dispatchFieldUpdate("surname", payload)
      this.dispatchFieldUpdate("name", payload)
    } catch (error) {
      this.setStatus(error.message || "Speichern fehlgeschlagen.", true)
    } finally {
      this.setBusy(false)
    }
  }

  applyValues(firstName, lastName) {
    this.firstNameValue = firstName == null ? "" : String(firstName)
    this.lastNameValue = lastName == null ? "" : String(lastName)
    this.data.set("firstName", this.firstNameValue)
    this.data.set("lastName", this.lastNameValue)

    this.updateText(this.firstNameTextTarget, this.firstNameValue, this.data.get("firstNameLabel") || "Vorname")
    this.updateText(this.lastNameTextTarget, this.lastNameValue, this.data.get("lastNameLabel") || "Nachname")
  }

  updateText(target, value, fallback) {
    target.textContent = value || fallback
    target.classList.toggle("project-muted", !value)
  }

  dispatchFieldUpdate(field, payload) {
    const values = payload.values || {}
    const displayHtmls = payload.display_htmls || {}

    document.dispatchEvent(new CustomEvent("inline-lead-edit:saved", {
      detail: {
        source: this.element,
        payload: {
          id: this.data.get("leadId"),
          field: field,
          value: Object.prototype.hasOwnProperty.call(values, field) ? values[field] : "",
          display_html: displayHtmls[field]
        }
      }
    }))
  }

  closeEditor() {
    this.editing = false
    this.element.classList.remove("is-editing")
    this.displayTarget.classList.remove("d-none")
    this.editButtonTarget.classList.remove("d-none")
    this.editorTarget.classList.add("d-none")
    this.firstNameTarget.value = this.firstNameValue
    this.lastNameTarget.value = this.lastNameValue
    this.clearStatus()
  }

  setBusy(isBusy) {
    this.busy = isBusy
    this.element.classList.toggle("is-saving", isBusy)
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = isBusy
    if (this.hasFirstNameTarget) this.firstNameTarget.disabled = isBusy
    if (this.hasLastNameTarget) this.lastNameTarget.disabled = isBusy
    if (isBusy) this.setStatus("Speichert ...", false)
  }

  flashSaved() {
    this.element.classList.add("is-saved")
    window.setTimeout(() => this.element.classList.remove("is-saved"), 900)
  }

  setStatus(message, isError) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("is-error", Boolean(isError))
  }

  clearStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.classList.remove("is-error")
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.getAttribute("content") : ""
  }
}
